import React from "react";

import { colors as colorTokens } from "@zapalacorp/tokens";
import { ChartDataset } from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { IDashboardData } from "../../../../models/overview/dashboard";
import NotEnoughData from "../../../DashMetrics/NotEnoughData";

const LIMIT_OF_CATEGORIES = 5;

interface Props {
  colors?: string[] | string;
  datasets: IDashboardData[];
  labels: string[];
}

const OverviewBarGraph = ({ colors = colorTokens.brandBlue500, datasets, labels }: Props) => {
  const { t: translate } = useTranslation();
  const { length } = datasets[0].data;

  if (!length) return <NotEnoughData />;

  const isMultiDimensional: boolean = datasets.length > 1;

  const options = {
    barPercentage: 1.0,
    categoryPercentage: 0.75,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        align: "start" as const,
        display: isMultiDimensional,
        labels: { padding: 8 },
        position: "bottom" as const,
      },
    },
    responsive: true,
    scales: { y: { grid: { display: false }, ticks: { padding: 0 } } },
  };

  if (!isMultiDimensional) {
    const dimensions: any[] = [];

    for (let i = 0; i < length; i += 1) {
      datasets.forEach((dataset, dimension_idx) => {
        const value = dataset.data[i];

        if (i === 0) {
          dimensions[dimension_idx] = {
            data: new Array(LIMIT_OF_CATEGORIES + 1).fill(0), // +1 because of "Others" category.
            label: dataset.label,
          };
        }

        if (i < LIMIT_OF_CATEGORIES) {
          dimensions[dimension_idx].data[i] = value;
        } else {
          dimensions[dimension_idx].data[LIMIT_OF_CATEGORIES] += value;
        }
      });
    }
    datasets = [...dimensions];

    if (length > LIMIT_OF_CATEGORIES) {
      labels = [...labels.slice(0, LIMIT_OF_CATEGORIES), translate("others").toUpperCase()];
      if (colors instanceof Array) {
        colors = [...colors.slice(0, LIMIT_OF_CATEGORIES), colorTokens.gray300];
      }
    }
  }

  return (
    <Bar
      data={{
        datasets: datasets.map((dataset, idx) => ({
          ...dataset,
          backgroundColor: isMultiDimensional ? colors[idx] : colors,
        })) as ChartDataset<"bar">[],
        labels: labels || new Array<string>(datasets.length).fill(""),
      }}
      options={options}
    />
  );
};

export default OverviewBarGraph;
